<template>
  <HrPageLayout
    title="Cabinet management"
    subtitle="County Executive sessions, cabinet papers, filings, and inward correspondence — County Secretariat record."
    icon=""
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'County HRMIS (SR)' },
      { label: 'Cabinet' }
    ]"
  >
    <div v-if="!allowed" class="cab-access-denied">
      <i class="bx bx-lock-alt"></i>
      You do not have access to this module.
    </div>
    <div v-else class="cab-shell">
      <section class="cab-hero" aria-label="Module introduction">
        <p class="cab-lead">
          Central register for cabinet cycle administration: schedule meetings, track departmental papers,
          file reports and documents, and manage external communications with a clear audit trail.
        </p>
      </section>

      <nav class="cab-tab-bar" aria-label="Cabinet sections">
        <button
          v-for="t in tabDefs"
          :key="t.id"
          type="button"
          class="cab-tab"
          :class="{ 'cab-tab--active': tab === t.id }"
          @click="setTab(t.id)"
        >
          <i class="bx" :class="t.icon" aria-hidden="true"></i>
          {{ t.label }}
        </button>
      </nav>

      <!-- Meetings -->
      <section v-show="tab === 'm'" class="cab-panel" aria-labelledby="cab-heading-m">
        <header class="cab-toolbar">
          <h2 id="cab-heading-m" class="cab-panel-title">Cabinet meetings & notices</h2>
          <div class="cab-toolbar-actions">
            <button v-if="canManage" type="button" class="cab-btn cab-btn--primary" @click="openMeeting()">
              <i class="bx bx-plus-circle"></i> Add meeting
            </button>
            <button type="button" class="cab-btn cab-btn--outline" @click="loadMeetings">
              <i class="bx bx-refresh"></i> Refresh
            </button>
          </div>
        </header>
        <div class="cab-table-card">
          <div v-if="load.m" class="cab-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading records…</div>
          <div v-else class="cab-table-scroll">
            <table class="cab-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Memo ref</th>
                  <th>Paper deadline</th>
                  <th>Status</th>
                  <th>Guidelines</th>
                  <th v-if="canManage" class="cab-th-actions"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in meetings" :key="r.id">
                  <td>{{ r.meeting_date || '–' }}</td>
                  <td class="cab-clip">{{ r.memo_ref || '–' }}</td>
                  <td>{{ r.submission_deadline || '–' }}</td>
                  <td><span class="cab-pill" :class="meetingStatusClass(r.status)">{{ r.status || '–' }}</span></td>
                  <td class="cab-clip cab-muted">{{ r.agenda_guidelines || '–' }}</td>
                  <td v-if="canManage" class="cab-td-actions">
                    <button type="button" class="cab-btn cab-btn--sm cab-btn--ghost" @click="openMeeting(r)">Edit</button>
                  </td>
                </tr>
                <tr v-if="!meetings.length">
                  <td :colspan="canManage ? 6 : 5" class="cab-empty">No meetings recorded.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Papers -->
      <section v-show="tab === 'p'" class="cab-panel" aria-labelledby="cab-heading-p">
        <header class="cab-toolbar">
          <h2 id="cab-heading-p" class="cab-panel-title">Cabinet papers</h2>
          <div class="cab-toolbar-actions">
            <button v-if="canManage" type="button" class="cab-btn cab-btn--primary" @click="openPaper()">
              <i class="bx bx-plus-circle"></i> Add paper
            </button>
            <button type="button" class="cab-btn cab-btn--outline" @click="loadPapers">
              <i class="bx bx-refresh"></i> Refresh
            </button>
          </div>
        </header>
        <div class="cab-table-card">
          <div v-if="load.p" class="cab-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading records…</div>
          <div v-else class="cab-table-scroll">
            <table class="cab-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Department</th>
                  <th>Meeting</th>
                  <th>Status</th>
                  <th v-if="canManage" class="cab-th-actions"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in papers" :key="r.id">
                  <td class="cab-strong">{{ r.title }}</td>
                  <td>{{ deptLabel(r.department_id) }}</td>
                  <td class="cab-muted">{{ meetingLabel(r.meeting_id) }}</td>
                  <td><span class="cab-pill" :class="paperStatusClass(r.submission_status)">{{ r.submission_status }}</span></td>
                  <td v-if="canManage" class="cab-td-actions">
                    <button type="button" class="cab-btn cab-btn--sm cab-btn--ghost" @click="openPaper(r)">Edit</button>
                  </td>
                </tr>
                <tr v-if="!papers.length">
                  <td :colspan="canManage ? 5 : 4" class="cab-empty">No papers recorded.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- External comms -->
      <section v-show="tab === 'e'" class="cab-panel" aria-labelledby="cab-heading-e">
        <header class="cab-toolbar">
          <h2 id="cab-heading-e" class="cab-panel-title">External communications</h2>
          <div class="cab-toolbar-actions">
            <button v-if="canManage" type="button" class="cab-btn cab-btn--primary" @click="openComm()">
              <i class="bx bx-plus-circle"></i> Add communication
            </button>
            <button type="button" class="cab-btn cab-btn--outline" @click="loadComms">
              <i class="bx bx-refresh"></i> Refresh
            </button>
          </div>
        </header>
        <div class="cab-table-card">
          <div v-if="load.e" class="cab-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading records…</div>
          <div v-else class="cab-table-scroll">
            <table class="cab-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Source</th>
                  <th>Subject</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th v-if="canManage" class="cab-th-actions"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in comms" :key="r.id">
                  <td>{{ r.received_date || '–' }}</td>
                  <td>{{ r.source_label || '–' }}</td>
                  <td class="cab-clip">{{ r.subject || '–' }}</td>
                  <td>{{ deptLabel(r.department_id) }}</td>
                  <td><span class="cab-pill" :class="commStatusClass(r.status)">{{ r.status }}</span></td>
                  <td v-if="canManage" class="cab-td-actions">
                    <button type="button" class="cab-btn cab-btn--sm cab-btn--ghost" @click="openComm(r)">Edit</button>
                  </td>
                </tr>
                <tr v-if="!comms.length">
                  <td :colspan="canManage ? 6 : 5" class="cab-empty">No external communications recorded.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Documents -->
      <section v-show="tab === 'd'" class="cab-panel" aria-labelledby="cab-heading-d">
        <header class="cab-toolbar">
          <h2 id="cab-heading-d" class="cab-panel-title">Documents & reports</h2>
          <div class="cab-toolbar-actions">
            <button v-if="canManage" type="button" class="cab-btn cab-btn--primary" @click="openDoc()">
              <i class="bx bx-upload"></i> Upload document / report
            </button>
            <button type="button" class="cab-btn cab-btn--outline" @click="loadDocs">
              <i class="bx bx-refresh"></i> Refresh
            </button>
          </div>
        </header>
        <div class="cab-table-card">
          <div v-if="load.d" class="cab-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading records…</div>
          <div v-else class="cab-table-scroll">
            <table class="cab-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Title</th>
                  <th>Meeting</th>
                  <th>Paper</th>
                  <th>File</th>
                  <th>Uploaded by</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in docs" :key="r.id">
                  <td><span class="cab-pill cab-pill--muted">{{ r.document_category || '–' }}</span></td>
                  <td class="cab-strong cab-clip">{{ r.title || '–' }}</td>
                  <td class="cab-muted">{{ meetingLabel(r.meeting_id) }}</td>
                  <td class="cab-muted">{{ paperTitle(r.paper_id) }}</td>
                  <td>
                    <a class="cab-link" :href="fileUrl(r.file_path)" target="_blank" rel="noopener">
                      <i class="bx bx-link-external"></i> {{ r.file_name || 'Open file' }}
                    </a>
                  </td>
                  <td>{{ r.uploaded_by || '–' }}</td>
                </tr>
                <tr v-if="!docs.length">
                  <td colspan="6" class="cab-empty">No documents uploaded.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Modal -->
      <div v-if="modal.show" class="cab-modal-overlay" @click.self="modal.show = false">
        <div class="cab-modal" role="dialog" aria-modal="true">
          <div class="cab-modal-head">
            <h3 class="cab-modal-title">{{ modal.title }}</h3>
          </div>
          <div class="cab-modal-body">
            <div v-for="row in modal.fields" :key="row.key" class="cab-field">
              <label class="cab-label">{{ row.label }}</label>
              <select v-if="row.type === 'select'" v-model="modal.form[row.key]" class="cab-input">
                <option v-for="o in row.options" :key="o" :value="o">{{ o }}</option>
              </select>
              <select v-else-if="row.type === 'meeting'" v-model="modal.form[row.key]" class="cab-input">
                <option value="">— Select meeting —</option>
                <option v-for="m in meetings" :key="m.id" :value="String(m.id)">{{ m.meeting_date }} (ref. {{ m.memo_ref || m.id }})</option>
              </select>
              <select v-else-if="row.type === 'department'" v-model="modal.form[row.key]" class="cab-input">
                <option value="">— Select department —</option>
                <option v-for="d in departments" :key="d.id" :value="String(d.id)">{{ d.departmentName }}</option>
              </select>
              <select v-else-if="row.type === 'paper'" v-model="modal.form[row.key]" class="cab-input">
                <option value="">— Optional —</option>
                <option v-for="p in papers" :key="p.id" :value="String(p.id)">{{ p.title }} (ID {{ p.id }})</option>
              </select>
              <div v-else-if="row.type === 'recipients'" class="cab-recipients">
                <div class="cab-recipient-actions">
                  <button type="button" class="cab-btn cab-btn--sm cab-btn--outline" @click="selectAllRecipients">Select all</button>
                  <button type="button" class="cab-btn cab-btn--sm cab-btn--outline" @click="clearAllRecipients">Clear all</button>
                </div>
                <div class="cab-recipient-grid">
                  <label v-for="name in defaultRecipients" :key="name" class="cab-recipient-option">
                    <input type="checkbox" :value="name" v-model="modal.form.memo_recipients_selected">
                    <span>{{ name }}</span>
                  </label>
                </div>
                <div class="cab-add-custom">
                  <input v-model="modal.form.new_recipient" type="text" class="cab-input" placeholder="Add custom recipient">
                  <button type="button" class="cab-btn cab-btn--sm cab-btn--primary" @click="addCustomRecipient">Add</button>
                </div>
                <div v-if="customRecipients.length" class="cab-chips">
                  <span class="cab-chip" v-for="(name, i) in customRecipients" :key="name + '_' + i">
                    {{ name }}
                    <button type="button" class="cab-chip-x" @click="removeCustomRecipient(i)" aria-label="Remove">×</button>
                  </span>
                </div>
              </div>
              <input
                v-else-if="row.type === 'file'"
                type="file"
                class="cab-input cab-input-file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                @change="onModalFileChange($event)"
              >
              <textarea v-else-if="row.type === 'textarea'" v-model="modal.form[row.key]" class="cab-input cab-textarea" :rows="row.rows || 3" />
              <input v-else-if="row.type === 'date'" v-model="modal.form[row.key]" type="date" class="cab-input">
              <input v-else v-model="modal.form[row.key]" type="text" class="cab-input" :placeholder="row.placeholder || ''">
              <p v-if="row.help" class="cab-field-hint">{{ row.help }}</p>
            </div>
          </div>
          <div class="cab-modal-footer">
            <button type="button" class="cab-btn cab-btn--outline" @click="modal.show = false">Cancel</button>
            <button type="button" class="cab-btn cab-btn--primary" :disabled="modal.saving" @click="saveModal">{{ modal.saving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

function memoRecipientsToPlainText(val) {
  if (val == null || val === "") return defaultCabinetMemoRecipients().join("\n");
  if (typeof val === "string") {
    const t = val.trim();
    if (t.startsWith("[")) {
      try {
        const arr = JSON.parse(t);
        if (Array.isArray(arr)) return arr.map((x) => String(x).trim()).filter(Boolean).join("\n");
      } catch (_) { /* fall through */ }
    }
    return val;
  }
  if (Array.isArray(val)) return val.map((x) => String(x).trim()).filter(Boolean).join("\n");
  return String(val);
}

function plainTextToMemoRecipientsJson(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  return JSON.stringify(lines.length ? lines : defaultCabinetMemoRecipients());
}

function defaultCabinetMemoRecipients() {
  return [
    "The Governor",
    "Deputy Governor",
    "CECM Agriculture and Livestock",
    "CECM Finance and Economic Planning",
    "CECM Governance, Administration, Communication and Devolution",
    "CECM Education, Human Capital and Vocational Training",
    "CECM Roads, Public Works, Transport and Infrastructure",
    "CECM Youth, Sports, Gender Inclusivity, Cultural Heritage and Social Services",
    "CECM Water, Sanitation, Irrigation, Environment, Energy and Climate Change",
    "CECM Public Health and Medical Services",
    "CECM Lands, Physical Planning, Housing and Urban Development",
    "CECM Trade, Industry Tourism and Cooperative Development and Marketing",
    "CECM Blue Economy, Fisheries, Mining and Digital Economy",
    "County Attorney"
  ];
}

export default {
  name: "CabinetManagementModule",
  components: { HrPageLayout },
  data() {
    return {
      allowed: false,
      canManage: false,
      tab: "m",
      meetings: [],
      papers: [],
      docs: [],
      comms: [],
      departments: [],
      load: { m: false, p: false, d: false, e: false },
      modal: { show: false, title: "", fn: "", fields: [], form: {}, saving: false },
      defaultRecipients: defaultCabinetMemoRecipients(),
      tabDefs: [
        { id: "m", label: "Meetings", icon: "bx-calendar-event" },
        { id: "p", label: "Papers", icon: "bx-file-blank" },
        { id: "d", label: "Documents & reports", icon: "bx-folder-open" },
        { id: "e", label: "External comms", icon: "bx-envelope-open" }
      ]
    };
  },
  computed: {
    customRecipients() {
      const selected = Array.isArray(this.modal.form.memo_recipients_selected) ? this.modal.form.memo_recipients_selected : [];
      return selected.filter((n) => !this.defaultRecipients.includes(n));
    }
  },
  created() {
    this.allowed = getRight("CABINET_VIEW") || getRight("CABINET_MANAGE");
    this.canManage = getRight("CABINET_MANAGE");
    if (this.allowed) {
      this.fetchDepartments();
      this.loadMeetings();
    }
  },
  methods: {
    setTab(id) {
      this.tab = id;
      if (id === "m") this.loadMeetings();
      if (id === "p") this.loadPapers();
      if (id === "d") this.loadDocs();
      if (id === "e") this.loadComms();
    },
    deptLabel(id) {
      if (id == null || id === "") return "–";
      const d = this.departments.find((x) => String(x.id) === String(id));
      return d ? d.departmentName : String(id);
    },
    meetingLabel(mid) {
      if (mid == null || mid === "") return "–";
      const m = this.meetings.find((x) => String(x.id) === String(mid));
      return m ? `${m.meeting_date}${m.memo_ref ? " · " + m.memo_ref : ""}` : String(mid);
    },
    paperTitle(pid) {
      if (pid == null || pid === "") return "–";
      const p = this.papers.find((x) => String(x.id) === String(pid));
      return p ? p.title : String(pid);
    },
    meetingStatusClass(s) {
      const k = String(s || "").toLowerCase();
      if (k.includes("complet")) return "cab-pill--success";
      if (k.includes("cancel")) return "cab-pill--danger";
      if (k.includes("sched")) return "cab-pill--info";
      return "cab-pill--muted";
    },
    paperStatusClass(s) {
      const k = String(s || "").toLowerCase();
      if (k.includes("approv")) return "cab-pill--success";
      if (k.includes("reject")) return "cab-pill--danger";
      if (k.includes("draft")) return "cab-pill--warn";
      if (k.includes("review")) return "cab-pill--info";
      return "cab-pill--muted";
    },
    commStatusClass(s) {
      const k = String(s || "").toLowerCase();
      if (k.includes("clos")) return "cab-pill--muted";
      if (k.includes("progress")) return "cab-pill--info";
      if (k.includes("open")) return "cab-pill--warn";
      return "cab-pill--muted";
    },

    fetchDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    loadMeetings() {
      this.load.m = true;
      const fd = new FormData();
      fd.append("function", "getCabinetMeetingList");
      fd.append("limit", "100");
      fd.append("page", "1");
      execute(fd, authUrl).then((res) => {
        this.load.m = false;
        const d = res.data && res.data.success && res.data.data ? res.data.data.data : [];
        this.meetings = Array.isArray(d) ? d : [];
      }).catch(() => { this.load.m = false; });
    },
    loadPapers() {
      this.load.p = true;
      const fd = new FormData();
      fd.append("function", "getCabinetPaperList");
      fd.append("limit", "100");
      fd.append("page", "1");
      execute(fd, authUrl).then((res) => {
        this.load.p = false;
        const d = res.data && res.data.success && res.data.data ? res.data.data.data : [];
        this.papers = Array.isArray(d) ? d : [];
      }).catch(() => { this.load.p = false; });
    },
    loadComms() {
      this.load.e = true;
      const fd = new FormData();
      fd.append("function", "getCabinetExternalCommList");
      fd.append("limit", "100");
      fd.append("page", "1");
      execute(fd, authUrl).then((res) => {
        this.load.e = false;
        const d = res.data && res.data.success && res.data.data ? res.data.data.data : [];
        this.comms = Array.isArray(d) ? d : [];
      }).catch(() => { this.load.e = false; });
    },
    loadDocs() {
      this.load.d = true;
      const fd = new FormData();
      fd.append("function", "getCabinetDocumentList");
      fd.append("limit", "100");
      fd.append("page", "1");
      execute(fd, authUrl).then((res) => {
        this.load.d = false;
        const d = res.data && res.data.success && res.data.data ? res.data.data.data : [];
        this.docs = Array.isArray(d) ? d : [];
      }).catch(() => { this.load.d = false; });
    },
    openMeeting(r) {
      const recPlain = memoRecipientsToPlainText(r && r.memo_recipients_json ? r.memo_recipients_json : null);
      const selectedRecipients = recPlain.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      this.modal = {
        show: true,
        title: r ? "Edit meeting / notice" : "New meeting / internal notice",
        fn: "saveCabinetMeeting",
        fields: [
          { key: "meeting_date", label: "Cabinet meeting date", type: "date" },
          { key: "submission_deadline", label: "Paper submission deadline", type: "date" },
          { key: "status", label: "Status", type: "select", options: ["Scheduled", "Completed", "Cancelled"] },
          { key: "agenda_guidelines", label: "Agenda guidelines", type: "textarea", rows: 4, placeholder: "Enter agenda and submission guidance" },
          { key: "memo_ref", label: "Internal memo reference", type: "text", placeholder: "e.g. HB/CTY/CECM/VOL.I (57)" },
          { key: "memo_from", label: "Memo from", type: "text", placeholder: "County Secretary" },
          { key: "memo_date", label: "Memo date", type: "date" },
          { key: "memo_coopted", label: "Co-opted", type: "text", placeholder: "None" },
          { key: "memo_recipients_selected", label: "Distribution list", type: "recipients", help: "Select recipients and add any custom office or person." }
        ],
        form: {
          id: r ? r.id : "",
          meeting_date: (r && r.meeting_date) ? String(r.meeting_date).slice(0, 10) : "",
          submission_deadline: (r && r.submission_deadline) ? String(r.submission_deadline).slice(0, 10) : "",
          status: (r && r.status) || "Scheduled",
          agenda_guidelines: (r && r.agenda_guidelines) || "",
          memo_ref: (r && r.memo_ref) || "",
          memo_from: (r && r.memo_from) || "County Secretary",
          memo_date: (r && r.memo_date) ? String(r.memo_date).slice(0, 10) : "",
          memo_coopted: (r && r.memo_coopted) || "None",
          memo_recipients_selected: selectedRecipients,
          new_recipient: ""
        },
        saving: false
      };
    },
    openPaper(r) {
      if (!this.meetings.length) this.loadMeetings();
      this.modal = {
        show: true,
        title: r ? "Edit paper" : "New paper",
        fn: "saveCabinetPaper",
        fields: [
          { key: "meeting_id", label: "Cabinet meeting", type: "meeting", help: "Choose the relevant meeting for this paper." },
          { key: "department_id", label: "Department", type: "department" },
          { key: "title", label: "Paper title", type: "text", placeholder: "Enter cabinet paper title" },
          { key: "summary", label: "Executive summary", type: "textarea", rows: 4, placeholder: "Brief summary for quick review" },
          { key: "background_info", label: "Background and justification", type: "textarea", rows: 5, placeholder: "Provide context, legal basis, and rationale" },
          { key: "proposed_decisions", label: "Proposed decisions / resolutions", type: "textarea", rows: 5, placeholder: "List each decision clearly" },
          { key: "submission_status", label: "Status", type: "select", options: ["Draft", "Submitted", "Under review", "Approved", "Rejected"] }
        ],
        form: {
          id: r ? r.id : "",
          meeting_id: r && r.meeting_id ? String(r.meeting_id) : "",
          department_id: r && r.department_id ? String(r.department_id) : "",
          title: (r && r.title) || "",
          summary: (r && r.summary) || "",
          background_info: (r && r.background_info) || "",
          proposed_decisions: (r && r.proposed_decisions) || "",
          submission_status: (r && r.submission_status) || "Draft"
        },
        saving: false
      };
    },
    openComm(r) {
      this.modal = {
        show: true,
        title: r ? "Edit communication" : "New communication",
        fn: "saveCabinetExternalComm",
        fields: [
          { key: "source_label", label: "Source", type: "text" },
          { key: "received_date", label: "Date received", type: "date" },
          { key: "subject", label: "Subject", type: "text" },
          { key: "department_id", label: "Responsible department", type: "department" },
          { key: "action_required", label: "Action required", type: "textarea" },
          { key: "attachment_file", label: "Attachment", type: "file", help: "Allowed: PDF, DOC, DOCX." },
          { key: "status", label: "Status", type: "select", options: ["Open", "In progress", "Closed"] }
        ],
        form: {
          id: r ? r.id : "",
          source_label: (r && r.source_label) || "",
          received_date: (r && r.received_date) ? String(r.received_date).slice(0, 10) : "",
          subject: (r && r.subject) || "",
          department_id: r && r.department_id ? String(r.department_id) : "",
          action_required: (r && r.action_required) || "",
          attachment_file: null,
          attachment_name: (r && r.attachment_name) || "",
          attachment_path: (r && r.attachment_path) || "",
          status: (r && r.status) || "Open"
        },
        saving: false
      };
    },
    openDoc() {
      if (!this.meetings.length) this.loadMeetings();
      if (!this.papers.length) this.loadPapers();
      this.modal = {
        show: true,
        title: "Upload document/report",
        fn: "saveCabinetDocument",
        fields: [
          { key: "meeting_id", label: "Meeting item", type: "meeting", help: "Link to a cabinet meeting." },
          { key: "paper_id", label: "Related paper", type: "paper", help: "Optional: link to a specific paper." },
          { key: "department_id", label: "Department", type: "department" },
          { key: "document_category", label: "Category", type: "select", options: ["Report", "Document"] },
          { key: "title", label: "Title", type: "text", placeholder: "Enter document/report title" },
          { key: "description", label: "Description", type: "textarea", rows: 3, placeholder: "Optional notes" },
          { key: "file", label: "Upload file", type: "file", help: "PDF, DOC, or DOCX." }
        ],
        form: {
          meeting_id: "",
          paper_id: "",
          department_id: "",
          document_category: "Report",
          title: "",
          description: "",
          file: null
        },
        saving: false
      };
    },
    saveModal() {
      this.modal.saving = true;
      const fd = new FormData();
      fd.append("function", this.modal.fn);
      const form = { ...this.modal.form };
      if (this.modal.fn === "saveCabinetMeeting") {
        const selected = Array.isArray(form.memo_recipients_selected) ? form.memo_recipients_selected : [];
        form.memo_recipients_json = JSON.stringify(selected.filter(Boolean));
        delete form.memo_recipients_selected;
        delete form.new_recipient;
      }
      if (this.modal.fn === "saveCabinetDocument") {
        const f = form.file;
        if (!f) {
          this.modal.saving = false;
          alert("Please choose a PDF, DOC, or DOCX file to upload.");
          return;
        }
        delete form.file;
        fd.append("file", f);
      }
      if (this.modal.fn === "saveCabinetExternalComm") {
        const f = form.attachment_file;
        if (f) fd.append("attachment_file", f);
        delete form.attachment_file;
      }
      Object.keys(form).forEach((k) => {
        fd.append(k, form[k] == null ? "" : form[k]);
      });
      execute(fd, authUrl).then((res) => {
        this.modal.saving = false;
        if (res.data && res.data.success) {
          this.modal.show = false;
          if (this.tab === "m") this.loadMeetings();
          if (this.tab === "p") this.loadPapers();
          if (this.tab === "d") this.loadDocs();
          if (this.tab === "e") this.loadComms();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Save failed");
        }
      }).catch(() => { this.modal.saving = false; });
    },
    onModalFileChange(event) {
      const f = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      if (!f) {
        if (this.modal.fn === "saveCabinetExternalComm") this.modal.form.attachment_file = null;
        else this.modal.form.file = null;
        return;
      }
      const name = String(f.name || "").toLowerCase();
      if (!(/\.(pdf|doc|docx)$/.test(name))) {
        alert("Only PDF, DOC, and DOCX files are allowed.");
        event.target.value = "";
        if (this.modal.fn === "saveCabinetExternalComm") this.modal.form.attachment_file = null;
        else this.modal.form.file = null;
        return;
      }
      if (this.modal.fn === "saveCabinetExternalComm") this.modal.form.attachment_file = f;
      else this.modal.form.file = f;
    },
    fileUrl(path) {
      const p = String(path || "");
      if (!p) return "#";
      if (/^https?:\/\//i.test(p)) return p;
      const base = authUrl.replace(/\/?index\.php$/i, "/");
      return base + p.replace(/^\/+/, "");
    },
    selectAllRecipients() {
      this.modal.form.memo_recipients_selected = [...this.defaultRecipients];
    },
    clearAllRecipients() {
      this.modal.form.memo_recipients_selected = [];
    },
    addCustomRecipient() {
      const v = String(this.modal.form.new_recipient || "").trim();
      if (!v) return;
      const selected = Array.isArray(this.modal.form.memo_recipients_selected) ? this.modal.form.memo_recipients_selected : [];
      if (!selected.includes(v)) selected.push(v);
      this.modal.form.memo_recipients_selected = selected;
      this.modal.form.new_recipient = "";
    },
    removeCustomRecipient(idx) {
      const custom = this.customRecipients;
      const name = custom[idx];
      if (!name) return;
      this.modal.form.memo_recipients_selected = (this.modal.form.memo_recipients_selected || []).filter((x) => x !== name);
    }
  }
};
</script>

<style scoped>
/* County / institutional shell — aligned with Skills Audit & SR modules */
.cab-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding-bottom: 40px;
}

.cab-access-denied {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 22px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
}

.cab-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 55%, #0f5980 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 10px 32px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.cab-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.95;
}

.cab-tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.cab-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.cab-tab:hover {
  background: rgba(255, 255, 255, 0.85);
  color: #0d4f73;
}

.cab-tab--active {
  background: #fff;
  color: #0d4f73;
  box-shadow: 0 2px 10px rgba(13, 79, 115, 0.12);
}

.cab-tab .bx {
  font-size: 18px;
  opacity: 0.9;
}

.cab-panel {
  margin-bottom: 8px;
}

.cab-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.cab-panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.cab-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.cab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.cab-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cab-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}

.cab-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}

.cab-btn--ghost {
  background: transparent;
  color: #0d4f73;
  border: 1px solid #cbd5e1;
}

.cab-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
}

.cab-btn--sm {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 8px;
}

.cab-table-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 18px rgba(13, 79, 115, 0.07);
  overflow: hidden;
}

.cab-table-scroll {
  overflow-x: auto;
}

.cab-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.cab-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.cab-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.cab-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}

.cab-table tbody tr:hover {
  background: #fafbfc;
}

.cab-th-actions,
.cab-td-actions {
  width: 100px;
  text-align: right;
  white-space: nowrap;
}

.cab-strong {
  font-weight: 600;
  color: #0f172a;
}

.cab-muted {
  color: #64748b;
  font-size: 13px;
}

.cab-clip {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .cab-clip {
    max-width: 160px;
  }
}

.cab-empty {
  text-align: center;
  color: #94a3b8;
  padding: 28px 16px !important;
  font-size: 14px;
}

.cab-loading {
  text-align: center;
  padding: 28px;
  color: #64748b;
  font-size: 14px;
}

.cab-loading .bx-spin {
  vertical-align: middle;
  margin-right: 8px;
}

.cab-pill {
  display: inline-block;
  padding: 4px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.cab-pill--info {
  background: #e0f2fe;
  color: #0369a1;
}

.cab-pill--success {
  background: #ecfdf5;
  color: #047857;
}

.cab-pill--danger {
  background: #fef2f2;
  color: #b91c1c;
}

.cab-pill--warn {
  background: #fef3c7;
  color: #92400e;
}

.cab-pill--muted {
  background: #f1f5f9;
  color: #64748b;
}

.cab-link {
  color: #0d4f73;
  font-weight: 600;
  text-decoration: none;
}

.cab-link:hover {
  text-decoration: underline;
}

/* Modal */
.cab-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.cab-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 560px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(13, 79, 115, 0.2);
  border: 1px solid #e2e8f0;
}

.cab-modal-head {
  padding: 18px 22px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.cab-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0d4f73;
}

.cab-modal-body {
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
}

.cab-modal-footer {
  padding: 14px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #fafbfc;
}

.cab-field {
  margin-bottom: 14px;
}

.cab-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cab-input,
.cab-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
}

.cab-input:focus,
.cab-textarea:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.cab-input-file {
  padding: 8px;
}

.cab-field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
}

.cab-recipients {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fafc;
}

.cab-recipient-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.cab-recipient-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  margin-bottom: 10px;
}

@media (max-width: 520px) {
  .cab-recipient-grid {
    grid-template-columns: 1fr;
  }
}

.cab-recipient-option {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #334155;
}

.cab-add-custom {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 8px;
}

.cab-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cab-chip {
  background: #e0f2fe;
  border: 1px solid #bae6fd;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #0c4a6e;
}

.cab-chip-x {
  border: none;
  background: transparent;
  color: #0369a1;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
}
</style>
